<template>
  <GeojsonLayer
    :source="source"
    :sourceId="source?.data?.id"
    :layer="layerStyleCluster"
    @layerClicked="onClusterClicked"
    @layerMouseEnter="handleMouseMoveCluster"
    @layerMouseLeave="handleMouseLeaveCluster"
    @layerMouseMove="handleMouseMoveCluster"
  />
  <div v-show="false">
    <MapPopup ref="popupRef" :rows="rows" />
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useStore } from 'vuex'
import mapboxgl, { GeoJSONSource, GeoJSONSourceSpecification, MapMouseEvent } from 'mapbox-gl'
import GeojsonLayer, { AnyLayerNotCustom } from '@/components/map/mapbox-layers/GeojsonLayer.vue'
import { MAPVIEW_PREFIX } from '@/store/module-prefixes'
import useMapbox, { zoomToEntities } from '@/composables/useMapbox'
import MapPopup from '../../popups/MapPopup.vue'
import { Row } from '@/components/Builder/BuilderRow.vue'
import { createSmallTitle } from '@/components/Builder'
import { useI18n } from 'vue-i18n'

const store = useStore()
const { t } = useI18n()

const props = defineProps<{
  source: GeoJSONSourceSpecification & { data: { id: string } }
}>()

const source = computed(() => props.source)

const popupRef = ref<InstanceType<typeof MapPopup> | null>(null)
const layerId = ref('complex-marker-cluster')
const popupCluster = ref(new mapboxgl.Popup({ closeButton: false, offset: 75 }))
const hoveredClusterNames = ref<Array<string>>([])
const hoveredClusterIds = ref<Array<string>>([])
const hoveredClusterId = ref<string | null>(null)
const { map } = useMapbox()

const isMeasurementActive = computed(() => store.getters.isMeasurementActive)
const selectedComplexName = computed(() => store.getters[`${MAPVIEW_PREFIX}/selectedComplexName`])
const clickedItemType = computed(() => store.state.clickedItemType)

const rows = computed<Row[]>(() =>
  hoveredClusterNames.value
    .slice()
    .sort()
    .slice(0, 5)
    .map(
      (name) =>
        ({
          cols: [
            {
              ...createSmallTitle(name).cols[0],
              style: {
                padding: '8px 0'
              }
            }
          ]
        } as Row)
    )
    .concat(hoveredClusterNames.value.length > 5 ? [createSmallTitle(t('general__text__etc'))] : [])
)

const layerStyleCluster = computed<AnyLayerNotCustom>(() => {
  return {
    id: layerId.value,
    source: source.value?.data?.id,
    type: 'symbol',
    maxzoom: 16,
    filter: ['has', 'point_count'],
    layout: {
      'icon-image': [
        'case',
        ['boolean', ['==', ['get', 'cluster_id'], hoveredClusterId.value], false],
        'complex_marker_shadow_active_large', // hover cluster
        ['all', isAllComplexesSelected.value, true],
        'complex_marker_shadow_active_large',
        ['all', ['==', isComplexOrMoreDetailedSelected.value, true]],
        'complex_marker_shadow_inactive_large', // selected complex
        'complex_marker_shadow_active_large'
      ],
      'icon-size': ['step', ['get', 'point_count'], 1, 1, 0.65, 2, 0.65, 5, 0.8],
      'icon-allow-overlap': true,
      'icon-anchor': 'bottom',
      'text-field': '{point_count_abbreviated}',
      'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
      'text-size': 18,
      'text-allow-overlap': true,
      'text-offset': [
        'step',
        ['get', 'point_count'],
        ['literal', [0, -2.4]],
        5,
        ['literal', [0, -2.95]]
      ]
    },
    paint: {
      'text-color': 'black'
    }
  }
})

const isAllComplexesSelected = computed(() => {
  return selectedComplexName.value === 'Alla' && clickedItemType.value === 'complex'
})

const isComplexOrMoreDetailedSelected = computed(() => {
  // Check for all types of (currently) possible markers
  return ['building', 'realEstate', 'complex'].indexOf(clickedItemType.value) !== -1
})

const onClusterClicked = (event: MapMouseEvent) => {
  if (!map) return
  if (isMeasurementActive.value) return
  const renderedFeatures = map.queryRenderedFeatures(event.point)
  if (renderedFeatures[0]?.properties?.cluster) {
    const clusterLayer = map.queryRenderedFeatures(event.point, {
      layers: [layerId.value]
    })
    if (!clusterLayer || clusterLayer.length === 0) return
    const clusterId = clusterLayer[0]?.properties?.cluster_id
    const pointCount = clusterLayer[0]?.properties?.point_count
    const clusterSource = map.getSource(source.value?.data?.id) as GeoJSONSource
    clusterSource.getClusterLeaves(clusterId, pointCount, 0, (err, features) => {
      if (err) return
      zoomToEntities(features.map((f) => f.properties?.id).filter((id) => id))
    })
  }
}

const handleMouseMoveCluster = (event: MapMouseEvent) => {
  if (!map) return
  if (isMeasurementActive.value || !isFirstLayer(event)) return
  setCursor('pointer')
  const clusterLayer = map.queryRenderedFeatures(event.point, {
    layers: [layerId.value]
  })
  if (!clusterLayer || clusterLayer.length === 0) return
  const coord = clusterLayer[0].geometry.coordinates
  const clusterId = clusterLayer[0].properties.cluster_id
  if (clusterId === hoveredClusterId.value) return
  hoveredClusterId.value = clusterId
  const clusterSource = map.getSource(source.value?.data?.id)
  hoveredClusterNames.value = []
  hoveredClusterIds.value = []
  fetchClusterData(clusterId, clusterSource, coord)
}

const handleMouseLeaveCluster = () => {
  if (isMeasurementActive.value) return
  hoveredClusterId.value = null
  setCursor('')
  popupCluster.value.remove()
}

const updateHoverCluster = (clusterNames: string[], coord: any) => {
  if (clusterNames && hoveredClusterId.value) showPopupCluster(clusterNames, coord)
}

const isFirstLayer = (event: MapMouseEvent) => {
  if (!map) return false
  const renderedFeatures = map.queryRenderedFeatures(event.point)
  return renderedFeatures[0].source === source.value?.data?.id
}

const showPopupCluster = (clusterNames: string[], coord: any) => {
  if (!map) return
  if (!popupRef.value) return
  popupCluster.value.setLngLat(coord).setDOMContent(popupRef.value.$el).addTo(map)
}

const fetchClusterData = (clusterId: string, clusterSource: any, coord: any) => {
  hoveredClusterIds.value.shift()
  clusterSource.getClusterChildren(clusterId, (err: any, features: any[]) => {
    if (err) return
    features.forEach((el) => {
      if (el.properties.title) {
        hoveredClusterNames.value.push(el.properties.title)
      }
      if (el.properties.cluster_id) {
        hoveredClusterIds.value.push(el.properties.cluster_id)
      }
    })
    if (hoveredClusterIds.value.length) {
      fetchClusterData(hoveredClusterIds.value[0], clusterSource, coord)
      return
    }
    updateHoverCluster(hoveredClusterNames.value, coord)
  })
}

const setCursor = (cursorType: string) => {
  if (!map) return
  map.getCanvas().style.cursor = cursorType
}
</script>
